import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
} from "@mui/material"
import Box from "@mui/material/Box"
import Typography from "@mui/material/Typography"
import React from "react"
import CloseIcon from "@mui/icons-material/Close"
import TrackChangesIcon from "@mui/icons-material/TrackChanges" // Замена для мишени (Entry Fee)
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents"
import AccessTimeIcon from "@mui/icons-material/AccessTime"
import LoadingButton from "@mui/lab/LoadingButton"
import { CircleCheck } from "lucide-react"
import Paper from "@mui/material/Paper"
import Button from "@mui/material/Button"
import { useNavigate } from "react-router-dom"
import { PATHS } from "../../../../constants/paths"
import { formatCountdown } from "../../utils/formatCountdown"
import { useCountdown } from "../../utils/useCountdown"
import { useTournamentDialogs } from "../../hooks/useTournamentDialogs"
import { GREEN_BUTTON_SX } from "../TournamentActionButton"

const PaymentSuccessDialog = ({
  tournament,
  user,
  closeDialog,
  backPath = PATHS.TOURNAMENTS.PATH,
  maxWidth = 550,
}) => {
  const remainingSeconds = useCountdown(tournament)

  const navigate = useNavigate()
  const { openGame } = useTournamentDialogs({ tournament, user })

  const handleBackClick = () => {
    closeDialog()
    navigate(backPath)

    setTimeout(() => {
      window.location.reload()
    }, 0)
  }

  return (
    <Box
      sx={{
        maxWidth,
        width: 500,
        textAlign: "center",
        border: "1px solid rgba(198, 146, 76, 0.25)",
        borderRadius: 1,
        backgroundColor: "#010101",
      }}
    >
      <DialogTitle sx={{ fontSize: "1rem", position: "relative", pr: 6 }}>
        <Typography sx={{ textTransform: "uppercase" }} variant="h1">
          Tournament joined!
        </Typography>

        <IconButton
          aria-label="close"
          onClick={closeDialog}
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            color: "#c7954f",
            "&:hover": {
              background: "rgba(214,154,73,0.08)",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 34 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 0 }}>
        <Typography
          sx={{
            color: "#dedede",
            fontSize: 18,
            lineHeight: 1.5,
          }}
        >
          You have successfully joined.
        </Typography>

        {/* Название турнира */}
        <Typography
          variant="h2"
          sx={{
            fontWeight: 900,
            letterSpacing: "0.05em",
            color: "#ffffff",
            textTransform: "uppercase",
            mb: 4,
            background: "#dedede",
            backgroundClip: "text",
          }}
        >
          {tournament.title}
        </Typography>

        {/* Блок с деталями турнира */}
        <Paper
          variant="outlined"
          sx={{
            borderColor: "rgba(194, 157, 83, 0.2)",
            backgroundColor: "#111",
            p: 2.5,
            mb: 3,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            width: "80%",
            mx: "auto",
          }}
        >
          {/* Entry Fee */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pb: 1,
              borderBottom: "1px solid #333",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <TrackChangesIcon sx={{ color: "#c29d53", fontSize: 22 }} />
              <Typography
                variant="body2"
                sx={{
                  color: "#dedede",
                  textTransform: "uppercase",
                  fontSize: 22,
                }}
              >
                Entry Fee
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: "#c29d53",
                fontWeight: "bold",
                fontSize: 22,
              }}
            >
              {tournament.entry_fee} USDT
            </Typography>
          </Box>

          {/* Prize Pool */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pb: 1,
              borderBottom: "1px solid #333",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <EmojiEventsIcon sx={{ color: "#c29d53", fontSize: 22 }} />
              <Typography
                variant="body2"
                sx={{
                  color: "#dedede",
                  textTransform: "uppercase",
                  fontSize: 22,
                }}
              >
                Prize Pool
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: "#c29d53",
                fontWeight: "bold",
                fontSize: 22,
              }}
            >
              {tournament.prize_pool} USDT
            </Typography>
          </Box>

          {/* Ends In */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <AccessTimeIcon sx={{ color: "#c29d53", fontSize: 22 }} />
              <Typography
                variant="body2"
                sx={{
                  color: "#dedede",
                  textTransform: "uppercase",
                  fontSize: 22,
                }}
              >
                Ends In
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{
                color: "#c29d53",
                fontWeight: "bold",
                fontSize: 22,
              }}
            >
              {formatCountdown(remainingSeconds)}
            </Typography>
          </Box>
        </Paper>

        {/* Иконка галочки в круге подтверждения */}
        <Box
          sx={{
            "& svg": {
              width: 70,
              height: 70,
              color: "#c29d53",
            },
          }}
        >
          <CircleCheck />
        </Box>

        {/* Текст подтверждения */}
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", mb: 1, fontSize: 18 }}
        >
          Your entry has been confirmed.
        </Typography>

        <Divider
          sx={{
            borderColor: "rgba(255,255,255,0.1)",
            width: "60%",
            mx: "auto",
            mb: 2,
          }}
        />

        {/* Пожелание удачи */}
        <Typography
          variant="body2"
          sx={{ color: "#c29d53", fontWeight: "bold", fontSize: 22 }}
        >
          Good luck, survivor.
        </Typography>
      </DialogContent>

      <DialogActions
        sx={{ p: 3, width: "100%", display: "flex", flexDirection: "column" }}
      >
        <LoadingButton
          variant="contained"
          size="large"
          fullWidth
          sx={{ ...GREEN_BUTTON_SX, fontSize: 22 }}
          onClick={(event) => {
            event.stopPropagation()
            openGame()
          }}
        >
          Play Now
        </LoadingButton>

        <Button
          variant="contained"
          size="large"
          fullWidth
          onClick={handleBackClick}
          sx={{
            background: "linear-gradient(135deg, #191919, #101010)",
            mt: 2,
            ml: "0 !important",
            zIndex: 0,
            fontSize: 22,
            fontWeight: 400,
            "&::before": {
              content: '""',
              borderRadius: "inherit",
              position: "absolute",
              inset: 0,
              background: "linear-gradient(135deg, #242424, #151515)",
              opacity: 0,
              transition: "opacity 250ms cubic-bezier(0.4, 0, 0.2, 1)",
              zIndex: -1,
            },

            "&:hover::before": {
              opacity: 1,
            },
          }}
        >
          {backPath === PATHS.TOURNAMENTS.PATH
            ? "Back to tournaments"
            : "Go to tournament"}
        </Button>
      </DialogActions>
    </Box>
  )
}
export default PaymentSuccessDialog
